import { BookOpen, ExternalLink } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/utils/cn'

interface DocsLinkProps {
  to: string
  label?: string
  hint?: string
  className?: string
  size?: 'sm' | 'md'
}

export function DocsLink({ to, label = 'Docs', hint, className, size = 'sm' }: DocsLinkProps) {
  const [open, setOpen] = useState(false)

  const linkElement = (
    <Link
      to={to}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border transition-all',
        'border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/15 hover:border-amber-500/60',
        'text-amber-600 dark:text-amber-400 hover:text-amber-300',
        'shadow-[0_0_8px_rgba(245,158,11,0.15)] hover:shadow-[0_0_12px_rgba(245,158,11,0.25)]',
        size === 'sm' && 'px-2 py-1.5 text-xs md:py-0.5',
        size === 'md' && 'px-3 py-1 text-sm',
        className
      )}
      onClick={() => setOpen(false)}
    >
      <BookOpen className={cn(size === 'sm' ? 'h-3 w-3' : 'h-4 w-4')} />
      <span>{label}</span>
    </Link>
  )

  // If no hint provided, just render the link
  if (!hint) {
    return linkElement
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        asChild
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <span className="inline-flex">{linkElement}</span>
      </PopoverTrigger>
      <PopoverContent
        className="w-72 bg-card border-border p-3 z-[100]"
        side="bottom"
        align="start"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <p className="text-sm text-foreground leading-relaxed mb-3">{hint}</p>
        <Link
          to={to}
          className="inline-flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 hover:text-amber-300 transition-colors"
          onClick={() => setOpen(false)}
        >
          <span>Read full guide</span>
          <ExternalLink className="h-3 w-3" />
        </Link>
      </PopoverContent>
    </Popover>
  )
}
